import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, ArrowTabStepper, Button, Card, useToast } from '@kss/ui';
import { useAuth } from '@kss/auth';
import { ArrowLeft, Save, ShieldCheck, KeyRound, LogOut } from 'lucide-react';
import { authService } from '@kss/api';

/**
 * Logged-in user profile page. Reads identity from the JWT-backed
 * Redux session (no separate fetch — the live HRMS API doesn't ship a
 * `/me` endpoint). Exposes:
 *
 *   • Profile tab     — read-only summary (name / email / role /
 *                       tenant / permissions count / last issued).
 *   • Security tab    — change-password form against
 *                       POST /api/Auth/change-password.
 *   • Sessions tab    — placeholder until backend exposes
 *                       /api/Auth/sessions.
 */
export default function UserProfile() {
  const navigate = useNavigate();
  const toast = useToast();
  const { user, session, logout } = useAuth();

  const [tab, setTab] = useState(0);
  const tabs = [
    { key: 'profile',  label: 'Profile' },
    { key: 'security', label: 'Security' },
    { key: 'sessions', label: 'Sessions' },
  ];

  // Security form ------------------------------------------------------
  const [oldPwd, setOldPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const initial = (user?.name || '?').slice(0, 1).toUpperCase();
  const permsCount = useMemo(() => user?.permissions?.length ?? 0, [user]);

  async function handleChangePassword(e?: React.FormEvent) {
    e?.preventDefault();
    setErr(null);
    if (!oldPwd || !newPwd) {
      setErr('Both current and new password are required.');
      return;
    }
    if (newPwd.length < 8) {
      setErr('New password must be at least 8 characters.');
      return;
    }
    if (newPwd !== confirmPwd) {
      setErr("Confirmation doesn't match the new password.");
      return;
    }
    setSaving(true);
    try {
      const { apiClient } = await import('@kss/api');
      await apiClient.post('/Auth/change-password', {
        currentPassword: oldPwd,
        newPassword: newPwd,
      });
      toast.success('Password changed', 'Use your new password from the next sign-in.');
      setOldPwd('');
      setNewPwd('');
      setConfirmPwd('');
    } catch (e2) {
      const apiMsg = (e2 as {
        response?: { data?: { errors?: Record<string, string[]>; message?: string; title?: string } };
      })?.response?.data;
      const detail = apiMsg?.errors
        ? Object.entries(apiMsg.errors).map(([k, v]) => `${k}: ${v.join('; ')}`).join('  ·  ')
        : apiMsg?.message || apiMsg?.title;
      setErr(detail || (e2 instanceof Error ? e2.message : 'Could not change password'));
    } finally {
      setSaving(false);
    }
  }

  async function handleSignOut() {
    try {
      if (session?.sessionId) await authService.logout({ sessionId: session.sessionId });
    } catch {
      /* local logout proceeds anyway */
    }
    logout();
    navigate('/');
  }

  return (
    <div className="-m-6 p-[3px]">
      {err && (
        <Alert variant="danger" className="mb-3">
          {err}
        </Alert>
      )}

      <Card>
        <div className="mb-1.5 flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="inline-flex h-6 w-6 items-center justify-center rounded border border-border text-content-secondary hover:bg-surface-card-hover"
              title="Back"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
            </button>
            <h2 className="text-sm font-semibold text-content-primary">My profile</h2>
          </div>
          <Button variant="outline" size="sm" onClick={handleSignOut}>
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </Button>
        </div>

        {/* Identity strip */}
        <div className="mb-3 flex items-center gap-3 rounded-md border border-border bg-surface-card-hover p-3">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary text-base font-bold text-primary-foreground">
            {initial}
          </span>
          <div className="min-w-0 flex-1">
            <div className="truncate text-base font-semibold text-content-primary">
              {user?.name || '—'}
            </div>
            <div className="truncate text-xs text-content-secondary">{user?.email || '—'}</div>
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              {user?.role && (
                <span className="rounded-full bg-primary-light px-2 py-0.5 text-[10px] font-semibold text-primary">
                  {user.role}
                </span>
              )}
              <span className="rounded-full bg-surface-card px-2 py-0.5 text-[10px] text-content-muted">
                tenant {(user as { tenantId?: string } | null)?.tenantId?.slice(0, 8) ?? '—'}
              </span>
              <span className="rounded-full bg-surface-card px-2 py-0.5 text-[10px] text-content-muted">
                {permsCount} permissions
              </span>
            </div>
          </div>
        </div>

        <div className="mb-[2px]">
          <ArrowTabStepper
            compact
            steps={tabs.map((s) => ({ ...s, enabled: true }))}
            activeIndex={tab}
            onChange={setTab}
          />
        </div>

        {tab === 0 && (
          <div className="grid gap-3 text-sm">
            <Field label="Display name" value={user?.name} />
            <Field label="Email" value={user?.email} />
            <Field label="Role" value={user?.role} />
            <Field label="User id" mono value={(user as { id?: string } | null)?.id} />
            <Field label="Tenant id" mono value={(user as { tenantId?: string } | null)?.tenantId} />
            <Field label="Session id" mono value={session?.sessionId} />
            {session?.expiresAt && (
              <Field
                label="Access token expires"
                value={new Date(session.expiresAt).toLocaleString()}
              />
            )}
          </div>
        )}

        {tab === 1 && (
          <form onSubmit={handleChangePassword} className="grid gap-3">
            <h3 className="flex items-center gap-2 text-sm font-semibold text-content-primary">
              <KeyRound size={14} /> Change password
            </h3>
            <PwdInput label="Current password" value={oldPwd} onChange={setOldPwd} autoComplete="current-password" />
            <PwdInput label="New password (min 8 chars)" value={newPwd} onChange={setNewPwd} autoComplete="new-password" />
            <PwdInput label="Confirm new password" value={confirmPwd} onChange={setConfirmPwd} autoComplete="new-password" />
            <div className="flex justify-end">
              <Button onClick={() => handleChangePassword()} disabled={saving}>
                <Save className="h-4 w-4" /> {saving ? 'Saving…' : 'Update password'}
              </Button>
            </div>
          </form>
        )}

        {tab === 2 && (
          <div className="rounded-md border border-dashed border-border p-6 text-center text-sm text-content-muted">
            <ShieldCheck size={20} className="mx-auto mb-2 text-primary" />
            Active session list isn't exposed by the live HRMS API yet —
            this section will list every device signed into this account
            once <code>/api/Auth/sessions</code> ships.
          </div>
        )}
      </Card>
    </div>
  );
}

function Field({
  label,
  value,
  mono = false,
}: {
  label: string;
  value?: string | null;
  mono?: boolean;
}) {
  return (
    <div className="grid grid-cols-[160px_1fr] items-center gap-3 border-b border-border pb-2 last:border-b-0">
      <span className="text-xs font-medium text-content-secondary">{label}</span>
      <span className={mono ? 'font-mono text-xs text-content-primary' : 'text-content-primary'}>
        {value || '—'}
      </span>
    </div>
  );
}

function PwdInput({
  label,
  value,
  onChange,
  autoComplete,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  autoComplete?: string;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-content-secondary">{label}</span>
      <input
        type="password"
        autoComplete={autoComplete}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-10 w-full rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary"
      />
    </label>
  );
}
